// AaPreference — アンチエイリアス (AA、滑らかな線) の設定を per-user で
// localStorage に永続化する infrastructure 層ユーティリティ.
//
// 背景 (docs/_wip/modern-layout.md §9.9、consult 2026-06-08):
//   - AA は **β Modern feature** として導入 (`ModernToolProperties` のモディファイア枠で
//     on/off)。当初は Classic 非露出・常に false だった。
//   - 手ぶれ補正は engine.setStabilizer 経由で効く (= レイアウト非依存に効く) のに対し、
//     AA は **PenConfig.aa → stroke.start.aa の per-stroke param** として wire に乗る
//     (§9.9 決定4)。実効値は useRoomChrome が `pen.aa` に注入する。
//
// 【上書き consult 2026-06-13、modern-layout.md §1.1 表現パリティ】tri-state 化:
//   - 設定値モデルは **tri-state (未設定 | on | off)**。
//     - `getExplicit()` が `boolean | null` を返す (= null は「ユーザー未設定」)。
//     - 実効値 = `resolveEffectiveAa(explicit)`:
//       明示設定があればそれ (= **レイアウト間で共有**、ユーザーの選択を layout を
//       跨いで尊重)、未設定なら **共通既定 off** (= 旧 Java 互換の描き味)。
//   - **後方互換**: localStorage キー (`oekaki:aa-enabled`) と encoding ('on'|'off') は
//     旧実装のまま据え置き、「**保存値あり = 明示設定**」と再解釈する。
//
// 【再上書き consult 2026-06-13 続報】未設定の実効値は **レイアウト非依存の共通既定
// off** に統一 (= 当初の tri-state レイアウト別既定 Classic=off / Modern=on を撤廃):
//   - 未設定ユーザーがレイアウト切替で線の出力 (= キャンバスに乗る表現) が変わるのは
//     「レイアウト = 見せ方の差」(modern-layout.md §9.4) と矛盾するため。
//   - Modern は ModernToolProperties で設定が常時見えるので既定 off でも発見性は足りる
//     (ユーザー判断 2026-06-13)。
//   - 既存の保存値 (= 明示設定) の意味は不変。**未設定の Modern ユーザーだけ AA が
//     off に変わる** (許容済み)。手ぶれ補正 (`resolveEffectiveStabilizer`) と対称形。
//
// 設計 (`stabilizerPreference.ts` と同流儀):
//   - bool を文字列 (`'on' | 'off'`) で保存。parse 失敗 / 不在 / storage 不可は
//     **未設定 (null)** 扱い、例外を throw しない。
//   - StorageLike 抽象 + sentinel probe で Safari private browsing / Quota を弾く。
//   - store factory は test で storage mock を注入可能。
//
// Tests: aaPreference.test.ts (storage-injection style)。

/** localStorage キー: AA on/off (stabilizer の流儀に合わせる). 旧実装から不変. */
export const AA_ENABLED_KEY = 'oekaki:aa-enabled';

/**
 * 未設定時の共通既定 (= off、レイアウト非依存)。consult 2026-06-13 続報で
 * レイアウト別既定 (旧 AA_LAYOUT_DEFAULT、Modern=on) を撤廃: 触らなければ
 * 両レイアウトとも旧 Java 互換の描き味 (= aa なしの二値スタンプ)。
 */
export const DEFAULT_AA_ENABLED = false;

/**
 * localStorage キー: 「この端末の既定」(新しい端末に自動で付けた「あり」)。
 * 自分で選んだ値 (`AA_ENABLED_KEY`) とは **別キー**: 同じキーに書くと、自動の「あり」と
 * 自分で選んだ「あり」がリリース後に区別できなくなる (困りごとのコツの出し分け・計測に要る)。
 * 旧版クライアントからはこのキーは見えず、単に未設定 = なしになるだけで壊れない。
 */
export const AA_DEVICE_DEFAULT_KEY = 'oekaki:aa-device-default';

/**
 * 実効値の解決順: 自分で選んだ値 (explicit) → この端末の既定 (deviceDefault) →
 * `DEFAULT_AA_ENABLED` (off)。レイアウト非依存 (`resolveEffectiveStabilizer` と対称形)。
 */
export const resolveEffectiveAa = (
  explicit: boolean | null,
  deviceDefault: boolean | null = null,
): boolean => explicit ?? deviceDefault ?? DEFAULT_AA_ENABLED;

/**
 * Minimal Storage shape this module uses (stabilizerPreference.ts と同流儀).
 * `globalThis.localStorage` matches; tests pass a mock with the same surface.
 */
export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface AaPreferenceStore {
  /**
   * 明示設定を読み出し. 不在 / 不正 / storage 不可 → `null` (= 未設定).
   * 実効値が欲しい場合は `resolveEffectiveAa(getExplicit())` を使う.
   */
  getExplicit(): boolean | null;
  /** この端末の既定 (`AA_DEVICE_DEFAULT_KEY`、自動で付けた「あり」)。不在 / 不正 / storage 不可 → null. */
  getDeviceDefault(): boolean | null;
  /** この端末の既定を保存 (自分で選んだ値には触れない). storage 不可なら no-op. */
  setDeviceDefault(enabled: boolean): void;
  /** AA on/off を明示設定として保存. storage 不可なら no-op (例外を throw しない). */
  set(enabled: boolean): void;
  /** key を削除 (= 未設定に戻す). テスト / 設定リセット用. storage 不可なら no-op. */
  clear(): void;
}

/**
 * `globalThis.localStorage` をプローブして使用可能なら返す, さもなくば `null`.
 * stabilizerPreference.ts の probe を流用 (sentinel write + readback で Safari
 * private browsing / QuotaExceeded を弾く).
 */
const probeBrowserStorage = (): StorageLike | null => {
  try {
    const ls = globalThis.localStorage;
    if (!ls) return null;
    const sentinel = 'oekaki:aa-enabled__probe__';
    ls.setItem(sentinel, '1');
    const got = ls.getItem(sentinel);
    ls.removeItem(sentinel);
    if (got !== '1') return null;
    return ls;
  } catch {
    return null;
  }
};

export interface CreateAaPreferenceStoreOptions {
  /**
   * Override the underlying storage. Default: probe `globalThis.localStorage`,
   * fall through to no-op if unavailable. Pass `null` to **force** the no-op
   * path (= getter always returns null = 未設定, setter silently drops).
   *
   * テストでは小さな `Map` ベースの fake を渡す.
   */
  storage?: StorageLike | null;
}

const readAaExplicit = (storage: StorageLike): boolean | null => {
  try {
    const raw = storage.getItem(AA_ENABLED_KEY);
    if (raw === 'on') return true;
    if (raw === 'off') return false;
    // 不在 or 不正値 → 未設定 (= 共通既定 off に解決される)。
    return null;
  } catch {
    return null;
  }
};

const readAaDeviceDefault = (storage: StorageLike): boolean | null => {
  try {
    const raw = storage.getItem(AA_DEVICE_DEFAULT_KEY);
    if (raw === 'on') return true;
    if (raw === 'off') return false;
    return null;
  } catch {
    return null;
  }
};

const writeAaEnabled = (storage: StorageLike, enabled: boolean): void => {
  try {
    storage.setItem(AA_ENABLED_KEY, enabled ? 'on' : 'off');
  } catch {
    // QuotaExceeded / SecurityError 等 — 永続化できないだけで動作は継続させる.
  }
};

/**
 * `AaPreferenceStore` のインスタンスを構築. storage が unavailable な環境
 * (SSR / Safari private browsing 厳格モード) でも getter は null (= 未設定 →
 * 共通既定 off) を返すだけで、setter は no-op (= 例外で UI が壊れることはない).
 */
export const createAaPreferenceStore = (
  opts: CreateAaPreferenceStoreOptions = {},
): AaPreferenceStore => {
  const explicit = opts.storage;
  const storage = explicit === null ? null : (explicit ?? probeBrowserStorage());

  return {
    getExplicit() {
      if (storage === null) return null;
      return readAaExplicit(storage);
    },
    getDeviceDefault() {
      if (storage === null) return null;
      return readAaDeviceDefault(storage);
    },
    setDeviceDefault(enabled) {
      if (storage === null) return;
      try {
        storage.setItem(AA_DEVICE_DEFAULT_KEY, enabled ? 'on' : 'off');
      } catch {
        // ignore
      }
    },
    set(enabled) {
      if (storage === null) return;
      writeAaEnabled(storage, enabled);
    },
    clear() {
      if (storage === null) return;
      try {
        storage.removeItem(AA_ENABLED_KEY);
      } catch {
        // ignore
      }
    },
  };
};

/**
 * 新しい端末だけ AA の既定を「あり」にする (docs/_wip/aa-default-and-trouble-tips-2026-10-27.md §1)。
 * `returningVisitor` が `'first'` で、かつ端末の既定が未保存のときだけ、**別キー**
 * (`AA_DEVICE_DEFAULT_KEY`) に `on` を書く。自分で選んだ値 (`AA_ENABLED_KEY`) には書かない
 * (理由は `AA_DEVICE_DEFAULT_KEY` 参照)。`'returning'` / `'unknown'` / 保存済みは何もしない。
 * @returns 保存した (= 新しい端末として on を seed した) とき true
 */
export const seedAaForFirstVisitor = (
  visitor: 'first' | 'returning' | 'unknown',
  store: AaPreferenceStore,
): boolean => {
  if (visitor !== 'first') return false;
  if (store.getDeviceDefault() !== null) return false;
  store.setDeviceDefault(true);
  return true;
};
